import { useAuth } from 'hooks/useAuth';
import { useQueryState } from 'hooks/useQueryState';
import { useBillingState } from './useBillingState';
import { useBillingUsage } from './useBillingUsage';
import { useBillingAllowance } from './useBillingAllowance';
import { useClientUsage } from './useClientUsage';
import { useUsageTimeseries } from './useUsageTimeseries';
import { useInvoices } from './useInvoices';
import { useCheckout } from './useCheckout';
import { useUsageReport } from './useUsageReport';
import { useIsActivatingCheckout } from './useCheckoutReturn';

/**
 * Settings > Billing, as one manager: every query the page reads, the derived
 * decisions about what to show, and the agency's client drill-down.
 *
 * Each section reports its own loading and error state, so one slow or failed
 * request costs that section rather than blanking the page.
 *
 * An account's plan, allowance and credits are its own, so for an agency the page
 * has two views: its own (the agency fee, invoices, the usage report) and, once a
 * client is chosen in the Clients table, that client's plan, allowance and credits
 * with the picker to buy or change them. `state` and `allowance` describe the account
 * on show; `ownState` is always the caller's own.
 *
 * `?billingClient=` is read with a numeric default (0 = none). useQueryState
 * only coerces when its default is a number, so a `null` default handed back
 * the string "42" after a reload, and the selected row never matched its id.
 * It is screen-local, not global: it means nothing on any other page.
 */
export const useBillingSettings = () => {
  const { user } = useAuth();
  const isAgency = user?.company.type === 'Agency';
  const canManageBilling = !!user && user.role !== 'Member';
  const isActivating = useIsActivatingCheckout();

  const [billingClient, setBillingClient, , clearBillingClient] = useQueryState<number>('billingClient', 0);
  const selectedClientId =
    isAgency && typeof billingClient === 'number' && Number.isSafeInteger(billingClient) && billingClient > 0
      ? billingClient
      : null;

  const ownStateQuery = useBillingState();
  const stateQuery = useBillingState({ accountId: selectedClientId });
  const usageQuery = useBillingUsage();
  const allowanceQuery = useBillingAllowance({ accountId: selectedClientId });
  const clientUsageQuery = useClientUsage();
  const checkout = useCheckout();
  const usageReport = useUsageReport();

  const billingState = stateQuery.billingState;
  const ownState = ownStateQuery.billingState;
  // Who bills the caller's own account, which decides what is theirs to see: a client's users see no
  // invoices or usage report, since the agency is charged.
  const billedBy = ownState?.billedBy ?? null;
  const isBilledThroughAgency = !!billedBy;
  const billingMode = billingState?.billingMode ?? null;
  const viewingAgencyFee = !!billingState?.agencyFee;
  // An agency's own account holds the fee, not a plan: there is no plan to pick and no credits to buy.
  const isOnShowPayer = billingState?.isPayer ?? false;

  const invoicesQuery = useInvoices({ enabled: !!ownState && !isBilledThroughAgency });
  const hasInvoices = (invoicesQuery.invoices?.length ?? 0) > 0;
  const showInvoices = !!ownState && !isBilledThroughAgency && (ownState.billingMode === 'card' || hasInvoices);
  const showUsageReport = !!ownState && !isBilledThroughAgency;
  // A card account always has the card (an empty ledger is a state it explains); an invoice or free one has
  // it when the server gives it an allowance to show, with packs to buy on top. An agency's fee has none.
  const showTokens = !viewingAgencyFee && (billingMode === 'card' || !!billingState?.aiCredits);
  // Buying and changing a plan are Owner/Admin actions of whoever pays for the account on show.
  const showPlans =
    billingMode === 'card' && !viewingAgencyFee && canManageBilling && isOnShowPayer && !billingState?.planIncluded;
  const showAgencyFee = billingMode === 'card' && viewingAgencyFee && canManageBilling;

  const clients = clientUsageQuery.clientUsage?.clients;
  const selectedClientName = clients?.find((client) => client.companyId === selectedClientId)?.companyName ?? null;

  const selectClient = (companyId: number) =>
    companyId === selectedClientId ? clearBillingClient() : setBillingClient(companyId);

  const timeseriesQuery = useUsageTimeseries(selectedClientId);

  const usage = usageQuery.usage;
  const currency = billingState?.currency ?? null;
  const chartTitle = selectedClientId ? `Daily usage: ${selectedClientName ?? 'selected client'}` : 'Daily usage';

  return {
    user,
    isAgency,
    canManageBilling,
    isActivating,
    billedBy,
    accountId: selectedClientId,
    checkout,
    usageReport,
    currency,
    ownState,
    state: {
      billingState,
      isLoading: !billingState && !stateQuery.isError,
      isError: stateQuery.isError,
      retry: () => stateQuery.refetch(),
    },
    usage: {
      usage,
      isLoading: !usage && !usageQuery.isError,
      isError: usageQuery.isError,
      retry: () => usageQuery.refetch(),
    },
    allowance: {
      allowance: allowanceQuery.allowance,
      isLoading: allowanceQuery.isLoading,
      keepPlan: allowanceQuery.keepPlan,
      isKeepingPlan: allowanceQuery.isKeepingPlan,
    },
    clientUsage: {
      clientUsage: clientUsageQuery.clientUsage,
      isLoading: !clientUsageQuery.clientUsage && !clientUsageQuery.isError,
      isError: clientUsageQuery.isError,
      retry: () => clientUsageQuery.refetch(),
    },
    chart: {
      series: timeseriesQuery.timeseries?.series,
      // Zero-filled by the server, so "no usage" is a series of zeros, not an empty one.
      hasUsage: !!timeseriesQuery.timeseries?.series.some((point) => point.units > 0),
      title: chartTitle,
      isLoading: !timeseriesQuery.timeseries && !timeseriesQuery.isError,
      isError: timeseriesQuery.isError,
      retry: () => timeseriesQuery.refetch(),
    },
    invoices: {
      invoices: invoicesQuery.invoices,
      isLoading: !invoicesQuery.invoices && !invoicesQuery.isError,
      isError: invoicesQuery.isError,
      retry: () => invoicesQuery.refetch(),
    },
    showInvoices,
    showUsageReport,
    showTokens,
    showPlans,
    showAgencyFee,
    showClients: isAgency && !isBilledThroughAgency,
    selectedClientId,
    selectedClientName,
    selectClient,
    showAllClients: () => clearBillingClient(),
  };
};

export type BillingSettingsManager = ReturnType<typeof useBillingSettings>;

/** The shape every section's query slice has, for the shared loading and error lines. */
export type SectionStatus = { isLoading: boolean; isError: boolean; retry: () => void };
